import { useCallback, useContext, useState } from 'react';
import { useFloating, useClick, useDismiss, useInteractions } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReportFormat } from '@trinity/chat';
import type { QuantAnalysisItem } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import { QuantAnalysisContext, QuantAnalysisVersionContext } from '@trinity/chat-components';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import { ModalMoveQuantFile } from '@trinity/chat-components/components/QuantAnalysis/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useAsyncMutationDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { MoreVerticalAnchorSmall } from '@/components/presentation/Anchor';
import { useDeleteQuantAnalysisItemModal } from '@trinity/chat-components/hooks/useDeleteQuantAnalysisItemModal.js';
import { useEditQuantAnalysisItemModal } from '@trinity/chat-components/hooks/useEditQuantAnalysisItemModal.js';
import type { GroupTagUpdateItem } from '@trinity/chat-components/components/GroupTagSelect/index.js';
import { useUploadQuantAnalysisFileModal } from '@/containers/QuantAnalysis/hooks';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';

type Props = {
  className?: string;
  analysisItem: QuantAnalysisItem;
};

export const AnalysisContextMenu = (props: Props) => {
  const [open, setOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const zIndexMiddleware = useZIndexMiddleware();

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [zIndexMiddleware],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const quantAnalysis = useContext(QuantAnalysisContext);
  const projectContext = useContext(ProjectDetailsContext);
  const version = useContext(QuantAnalysisVersionContext);

  const projectId = projectContext.query.data?.project?.id;
  const [moveModalOpen, setMoveModalOpen] = useState(false);

  const apiClient = useChatApiClient();

  // Phase 3C: Modal hooks
  const [openDeleteModal, DeleteModal] = useDeleteQuantAnalysisItemModal({
    analysisItem: props.analysisItem,
    projectId,
    quantAnalysisId: quantAnalysis.id,
  });

  const [openUploadModal, UploadModal] = useUploadQuantAnalysisFileModal({
    projectId,
    quantAnalysisId: quantAnalysis.id,
  });

  const handleFetchTags = useCallback(async () => {
    const response = await apiClient.tags.getQuantAnalysisItemTags({
      itemId: props.analysisItem.id,
    });
    return response.tags;
  }, [apiClient, props.analysisItem.id]);

  const handleSearchTags = useCallback(
    async (query: string) => {
      const response = await apiClient.search.searchTags({ query });
      return response.results.map((r) => ({ id: r.id, name: r.name }));
    },
    [apiClient]
  );

  const handleSubmit = useCallback(
    async (data: { name: string; description: string; tags: GroupTagUpdateItem[] }) => {
      await apiClient.quantAnalysis.updateQuantAnalysisItem({
        projectId,
        quantAnalysisId: quantAnalysis.id,
        itemId: props.analysisItem.id,
        name: data.name,
        description: data.description,
        tags: data.tags,
      });
    },
    [apiClient, projectId, quantAnalysis.id, props.analysisItem.id]
  );

  const EditModal = useEditQuantAnalysisItemModal({
    analysisItem: props.analysisItem,
    projectId,
    quantAnalysisId: quantAnalysis.id,
    isOpen: editModalOpen,
    onClose: () => setEditModalOpen(false),
    onFetchTags: handleFetchTags,
    onSearchTags: handleSearchTags,
    onSubmit: handleSubmit,
  });

  const toggleDeleteModal = useCallback(() => {
    if (projectId && openDeleteModal) {
      openDeleteModal();
    }
    handleBlur();
  }, [openDeleteModal, handleBlur, projectId]);

  const toggleEditModal = useCallback(() => {
    if (projectId) {
      setEditModalOpen(true);
    }
    handleBlur();
  }, [handleBlur, projectId]);

  const toggleUploadModal = useCallback(() => {
    if (projectId && openUploadModal) {
      openUploadModal();
    }
    handleBlur();
  }, [openUploadModal, handleBlur, projectId]);

  const { mutateAsync: startReparsingFile } = useAsyncMutationDownloader({
    mutationFn: async () => {
      if (!version?.versionNumber) {
        throw new Error('Version number not available');
      }

      const response = await apiClient.quantAnalysis.reparseQuantAnalysisItem({
        projectId,
        quantAnalysisId: quantAnalysis.id,
        itemId: props.analysisItem.id,
        versionNumber: version.versionNumber,
      });
      return response.data;
    },
  });

  const handleReparseFile = useCallback(() => {
    const format = ReportFormat.Excel;
    startReparsingFile({
      meta: {
        format,
        name: quantAnalysis.name,
        title: `Uploading Analysis File`,
      },
    });
    handleBlur();
  }, [handleBlur, quantAnalysis, startReparsingFile]);

  return (
    <>
      <div className={props.className} ref={refs.setReference} {...getReferenceProps()}>
        <MoreVerticalAnchorSmall open={open} />
      </div>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleBlur} touchEvent="onTouchStart">
            <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <PopperMenu>
                <PopperMenuItem onClick={toggleEditModal}>Edit</PopperMenuItem>
                <PopperMenuItem onClick={toggleDeleteModal}>Delete</PopperMenuItem>
                <PopperMenuItem onClick={toggleUploadModal}>Upload Version</PopperMenuItem>
                <PopperMenuItem
                  onClick={handleReparseFile}
                  disabled={
                    !version?.versionNumber || version?.originalFileName?.toLowerCase().endsWith('.json')
                  }
                >
                  Reparse
                </PopperMenuItem>

                <PopperMenuItem
                  onClick={() => {
                    setMoveModalOpen(true);
                  }}
                >
                  Move
                </PopperMenuItem>
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
      {/* Phase 3C modals */}
      {EditModal}
      {DeleteModal}
      {UploadModal}
      {
        <ModalMoveQuantFile
          open={moveModalOpen}
          onClose={() => setMoveModalOpen(false)}
          quantAnalysisId={quantAnalysis.id}
          projectId={projectId}
          itemId={props.analysisItem.id}
          itemName={props.analysisItem.name}
        />
      }
    </>
  );
};
